import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CreditCard, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  FileText,
  User,
  Calendar,
  Shield
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PHSPayInvoicePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (token) {
      loadInvoice();
    } else {
      setError('Invalid payment link');
      setLoading(false);
    }
  }, [token]);

  const loadInvoice = async () => {
    try {
      const res = await axios.get(`${API}/api/phs/members/public/invoice?token=${token}`);
      setInvoice(res.data);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load invoice';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    setProcessing(true);
    setError(null);
    
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/api/phs/members/public/invoice/checkout`, null, {
        params: { token, origin_url: originUrl }
      });
      
      // Redirect to Stripe Checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#0F766E]" />
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Error</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 py-8 px-4">
      {/* Header */}
      <div className="max-w-lg mx-auto mb-8 text-center">
        <div className="w-20 h-20 mx-auto mb-4 bg-[#0F766E] rounded-2xl flex items-center justify-center shadow-lg">
          <img src="/phs-logo.png" alt="PHS" className="w-16 h-16 object-contain" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Park Home Support</h1>
        <p className="text-gray-500">Secure Invoice Payment</p>
      </div>

      {/* Invoice Card */}
      <Card className="max-w-lg mx-auto shadow-xl" data-testid="pay-invoice-card">
        <CardHeader className="bg-[#0F766E] text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Invoice Payment</CardTitle>
              <CardDescription className="text-white/80">{invoice?.invoice_number}</CardDescription>
            </div>
            <FileText className="h-8 w-8 opacity-80" />
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* Member Info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <User className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Member</p>
              <p className="font-medium text-gray-900">{invoice?.member_name}</p>
            </div>
          </div>

          {/* Task Reference */}
          {invoice?.task_id && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <FileText className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Task Reference</p>
                <p className="font-mono text-sm font-medium text-gray-900">{invoice.task_id}</p>
              </div>
            </div>
          )}

          {/* Due Date */}
          {invoice?.due_date && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Due Date</p>
                <p className="font-medium text-gray-900">
                  {new Date(invoice.due_date).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Amount */}
          <div className="p-6 bg-gradient-to-br from-[#0F766E]/10 to-[#0F766E]/5 rounded-xl text-center border border-[#0F766E]/20">
            <p className="text-sm text-gray-600 mb-1">Amount Due</p>
            <p className="text-4xl font-bold text-[#0F766E]">
              £{invoice?.amount?.toFixed(2) || '0.00'}
            </p>
            <Badge className="mt-2 bg-amber-100 text-amber-700">Payment Required Before Work</Badge>
          </div>

          {/* Security Notice */}
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <Shield className="h-5 w-5 text-green-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-green-800">Secure Payment</p>
              <p className="text-green-600">Your payment is processed securely via Stripe. We never store your card details.</p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Pay Button */}
          <Button 
            onClick={handlePayNow}
            disabled={processing}
            className="w-full h-14 text-lg bg-[#0F766E] hover:bg-[#115E59]"
            data-testid="pay-now-btn"
          >
            {processing ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5 mr-2" />
                Pay £{invoice?.amount?.toFixed(2) || '0.00'} Now
              </>
            )}
          </Button>

          <p className="text-xs text-center text-gray-500">
            By proceeding, you agree to Park Home Support's terms of service.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
